import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface ZenInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const ZenInsights: React.FC<ZenInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Determine active bullet index
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Very subtle fade-in + translateY 10→0 over 15 frames
  const opacity = interpolate(elapsedFrames, [0, 15], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const translateY = interpolate(elapsedFrames, [0, 15], [10, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
      }}
    >
      {/* Outer card — transparent bg, ink border bottom */}
      <div
        style={{
          width: "100%",
          background: "transparent",
          borderBottom: "1px solid rgba(26,26,24,0.1)",
          padding: "40px 8px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        {/* Top label: red dot + section counter + brand badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                fontSize: "6px",
                color: "#c0392b",
                lineHeight: 1,
              }}
            >
              ●
            </span>
            <span
              style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: "11px",
                color: "#8c7b68",
                letterSpacing: "3px",
                textTransform: "lowercase",
              }}
            >
              {` section ${activeIndex + 1}`}
            </span>
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#1a1a18"
                accentColor="#c0392b"
                size={60}
                style="minimal"
                opacity={opacity}
              />
            ) : null;
          })()}
        </div>

        {/* Bullet text with entrance animation */}
        <div
          style={{
            opacity,
            transform: `translateY(${translateY}px)`,
            minHeight: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontSize: "52px",
              fontWeight: 400,
              color: "#1a1a18",
              lineHeight: 1.6,
              margin: 0,
              padding: 0,
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 700,
                color: "#c0392b",
                borderBottom: "2px solid #c0392b",
                paddingBottom: "2px",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>

        {/* Dot pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "6px",
            alignItems: "center",
          }}
        >
          {bullets.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === activeIndex ? 16 : 5,
                height: 5,
                borderRadius: 2,
                background:
                  i === activeIndex ? "#c0392b" : "rgba(26,26,24,0.15)",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
